import { Fragment } from 'react'

import Link from 'next/link'

import { cx } from '@linaria/core'

import { User } from '~/api-definition/definition'
import { AppImage } from '~/components/data-display/AppImage'
import { EditIcon, GiftIcon, ReplyIcon, ReportIcon } from '~/components/icons'
import { autoLink } from '~/libs/autolink'
import { formatRelativeDateTime } from '~/libs/date'
import { TweetOrReply, tweetHtmlId, tweetImageToSrc } from '~/libs/tweet'
import { userAttributesLabel, userProfileIcon } from '~/libs/user'
import { useReportDialog } from '~/provider/ReportDialogProvider'

import { GiftCard } from '../gifts/GiftCard'

export const TweetRow: React.FC<{
  tweetOrReply: TweetOrReply
  userMe: User | undefined
  onClickImage: (tweetOrReply: TweetOrReply, index: number) => void
  onEdit: (tweetOrReply: TweetOrReply) => void
  onReplyTo: (tweetOrReply: TweetOrReply) => void
  onGiftTo: (tweetOrReply: TweetOrReply) => void
}> = (props) => {
  // const imageLength = props.tweetOrReply.type === 'reply' ? 4 : 5
  const imageLength = 4 // tweet の場合も iPhone XR で5枚は見切れる
  const reportDialog = useReportDialog()

  if (props.tweetOrReply.value.user.muted) {
    return (
      <div className="rounded bg-[#EFEFEF] border border-gray p-4 mt-4">
        ブロックしているユーザーの投稿です
      </div>
    )
  }

  return (
    <div
      className={cx(
        `px-4 py-3 scroll-m-14`,
        props.tweetOrReply.type === 'tweet' && 'border-b border-gray',
        props.tweetOrReply.type === 'reply' && 'pr-0'
      )}
      data-tweet-id={tweetHtmlId(props.tweetOrReply)}
    >
      <div className="flex gap-3 overflow-hidden">
        <div>
          <Link href={`/foodies/${props.tweetOrReply.value.user.userName}`}>
            <AppImage
              variant={{
                type: 'profile',
                user: props.tweetOrReply.value.user,
              }}
              width={props.tweetOrReply.type === 'tweet' ? 50 : 40}
              height={props.tweetOrReply.type === 'tweet' ? 50 : 40}
              rounded="full"
              className="flex-shrink-0"
              alt={`${props.tweetOrReply.value.user.displayName}さんのプロフィール画像`}
              src={userProfileIcon(props.tweetOrReply.value.user)}
            />
          </Link>
        </div>
        <div className="flex-grow flex-shrink min-w-0">
          <div className="flex justify-between text-xs gap-x-2">
            <div className="truncate">
              <span className="font-bold">
                {props.tweetOrReply.value.user.displayName}
              </span>
              <span>{userAttributesLabel(props.tweetOrReply.value.user)}</span>
            </div>
            <div className="flex-shrink-0 gap-x-2 items-center flex">
              <span className="text-[#6C6C6C]">
                {formatRelativeDateTime(
                  {
                    sec: props.tweetOrReply.value.publishedAt,
                  },
                  {
                    omitCurrentDay: true,
                    omitCurrentYear: true,
                  }
                )}
              </span>
              <button
                type="button"
                className="outline-none"
                onClick={() => {
                  if ('tweetReplyId' in props.tweetOrReply.value) {
                    reportDialog.show({
                      type: 'tweet_reply',
                      tweetReplyId: props.tweetOrReply.value.tweetReplyId,
                    })
                  } else {
                    reportDialog.show({
                      type: 'tweet',
                      tweetId: props.tweetOrReply.value.tweetId,
                    })
                  }
                }}
              >
                <ReportIcon className="w-4 h-4 fill-[#E7E7E7]" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <div className="text-sm whitespace-pre-wrap break-all">
              {props.tweetOrReply.value.text.split('\n').map((line, i) => {
                const linkedLine = autoLink(line)
                return (
                  <Fragment key={i}>
                    <div
                      className={cx(
                        line === '' ? 'min-h-[12px]' : 'min-h-[18px]'
                      )}
                      dangerouslySetInnerHTML={{ __html: linkedLine }}
                    />
                  </Fragment>
                )
              })}
              {props.tweetOrReply.type === 'tweet' &&
                props.tweetOrReply.value.edited && (
                  <span className="mt-1 text-xs text-[#6C6C6C]">
                    （編集済）
                  </span>
                )}
              {props.tweetOrReply.value.state === 'deleted' && (
                <span className="text-[#666]">この投稿は削除されました</span>
              )}
              {props.tweetOrReply.value.images.length > 0 && (
                <div className="mt-3">
                  {props.tweetOrReply.value.images.length === 1 &&
                    props.tweetOrReply.value.images.map((image, i) => {
                      return (
                        <button
                          key={image.imageId}
                          type="button"
                          onClick={() =>
                            props.onClickImage(props.tweetOrReply, i)
                          }
                        >
                          <AppImage
                            variant={{
                              type: 'contain',
                            }}
                            width={320}
                            height={240}
                            rounded="md"
                            alt={`添付画像${i + 1}`}
                            src={tweetImageToSrc(image)}
                            imageClassName="w-[320px] h-[240px] object-cover"
                            className="bg-[#EAEAEA] rounded-md overflow-hidden"
                          />
                        </button>
                      )
                    })}
                  <div className="flex w-full gap-2">
                    {props.tweetOrReply.value.images.length !== 1 &&
                      props.tweetOrReply.value.images
                        .slice(0, imageLength)
                        .map((image, i) => {
                          return (
                            <button
                              key={image.imageId}
                              type="button"
                              className="relative w-[60px] h-[60px]"
                              onClick={() =>
                                props.onClickImage(props.tweetOrReply, i)
                              }
                            >
                              <AppImage
                                variant={{
                                  type: 'contain',
                                }}
                                width={60}
                                height={60}
                                rounded="none"
                                alt={`添付画像${i + 1}`}
                                src={tweetImageToSrc(image)}
                                imageClassName="w-[60px] h-[60px] object-cover"
                                className="bg-[#EAEAEA] rounded-md overflow-hidden"
                              />
                              {props.tweetOrReply.value.images.length >
                                imageLength &&
                                i === imageLength - 1 && (
                                  <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full text-white bg-black rounded-md bg-opacity-40">
                                    +
                                    {props.tweetOrReply.value.images.length -
                                      imageLength}
                                  </div>
                                )}
                            </button>
                          )
                        })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end mt-2 gap-x-5">
        <button onClick={() => props.onReplyTo(props.tweetOrReply)}>
          <ReplyIcon className="fill-[#ccc]" />
        </button>
        {props.userMe &&
          props.tweetOrReply.value.user.userId === props.userMe.userId && (
            <button onClick={() => props.onEdit(props.tweetOrReply)}>
              <EditIcon className="fill-[#ccc]" />
            </button>
          )}
        {props.userMe &&
          props.tweetOrReply.value.user.userId !== props.userMe.userId && (
            <button onClick={() => props.onGiftTo(props.tweetOrReply)}>
              <GiftIcon className="fill-[#ccc]" />
            </button>
          )}
      </div>
      {props.tweetOrReply.value.receivedGifts.length > 0 && (
        <div className="flex flex-col mt-3 gap-y-4 ml-7">
          {props.tweetOrReply.value.receivedGifts.map((gift, i) => {
            if (gift.fromUser.muted) {
              return (
                <div
                  key={i}
                  className="rounded bg-[#EFEFEF] border border-gray p-4"
                >
                  ブロックしているユーザーの投稿です
                </div>
              )
            }

            // gift.gift.giftId は一意じゃないので、key には使わないこと
            return (
              <div key={i} className="flex justify-between gap-x-3">
                <Link href={`/foodies/${gift.fromUser.userName}`}>
                  <AppImage
                    variant={{
                      type: 'profile',
                      user: gift.fromUser,
                    }}
                    width={props.tweetOrReply.type === 'tweet' ? 50 : 40}
                    height={props.tweetOrReply.type === 'tweet' ? 50 : 40}
                    rounded="full"
                    className="flex-shrink-0"
                    alt={`${props.tweetOrReply.value.user.displayName}さんのプロフィール画像`}
                    src={userProfileIcon(gift.fromUser)}
                  />
                </Link>
                <div className="flex flex-col min-w-0 flex-grow">
                  <div className="flex justify-between mb-2 text-xs gap-x-2">
                    <div className="truncate">
                      <span className="font-bold">
                        {gift.fromUser.displayName}
                      </span>
                      <span>{userAttributesLabel(gift.fromUser)}</span>
                    </div>
                    <span className="flex-shrink-0 text-[#6C6C6C]">
                      {formatRelativeDateTime(
                        { sec: gift.receivedAt },
                        { omitCurrentYear: true, omitCurrentDay: true }
                      )}
                    </span>
                  </div>
                  <GiftCard
                    fromUser={gift.fromUser}
                    toUser={props.tweetOrReply.value.user}
                    gift={gift.gift}
                    className="max-w-[320px]"
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
      <div className="ml-7">
        {props.tweetOrReply.type === 'tweet' &&
          props.tweetOrReply.value.replies.map((reply) => (
            <TweetRow
              key={reply.tweetReplyId}
              userMe={props.userMe}
              tweetOrReply={{ type: 'reply', value: reply }}
              onEdit={props.onEdit}
              onReplyTo={props.onReplyTo}
              onGiftTo={props.onGiftTo}
              onClickImage={props.onClickImage}
            />
          ))}
        {}
      </div>
    </div>
  )
}
